/**
 * ConsentGate — first-sign-in legal consent enforcement for ALL required
 * legal documents.
 *
 * After a user is authenticated this component fetches their current
 * consent status from `/api/legal/consent/:userId`. If the user has not
 * accepted the latest version of any required document — Privacy Policy,
 * Terms of Service, Medical Disclaimer or Cookie Policy — an unskippable
 * modal is displayed and access to the app is blocked until every
 * outstanding consent is recorded.
 *
 * Re-prompts automatically when policy versions are bumped on the backend
 * because the gate compares against the `currentVersions` payload returned
 * by the API.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'wouter';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/hooks/useLanguage';
import { apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ShieldCheck, Loader2, AlertTriangle } from 'lucide-react';

type RequiredDocType =
  | 'privacy_policy'
  | 'terms_of_service'
  | 'medical_disclaimer'
  | 'cookie_policy';

interface ConsentResponse {
  consents: Array<{
    documentType: string;
    documentVersion: string;
    revokedAt: string | null;
  }>;
  currentVersions: Record<RequiredDocType, string>;
  status: Record<RequiredDocType, boolean>;
  allRequired: boolean;
}

const REQUIRED_DOCS: ReadonlyArray<RequiredDocType> = [
  'privacy_policy',
  'terms_of_service',
  'medical_disclaimer',
  'cookie_policy',
];

const DOC_LINKS: Record<RequiredDocType, string> = {
  privacy_policy: '/privacy',
  terms_of_service: '/terms',
  medical_disclaimer: '/medical-disclaimer',
  cookie_policy: '/cookie-policy',
};

const DOC_LABELS: Record<RequiredDocType, { en: string; ar: string }> = {
  privacy_policy: { en: 'Privacy Policy', ar: 'سياسة الخصوصية' },
  terms_of_service: { en: 'Terms of Service', ar: 'شروط الخدمة' },
  medical_disclaimer: { en: 'Medical Disclaimer', ar: 'إخلاء المسؤولية الطبية' },
  cookie_policy: { en: 'Cookie Policy', ar: 'سياسة ملفات تعريف الارتباط' },
};

export function ConsentGate() {
  const { user, isAuthenticated } = useAuth();
  const { language, isRTL } = useLanguage();
  const queryClient = useQueryClient();
  const [accepted, setAccepted] = useState<Record<RequiredDocType, boolean>>({
    privacy_policy: false,
    terms_of_service: false,
    medical_disclaimer: false,
    cookie_policy: false,
  });
  const [error, setError] = useState<string | null>(null);

  const userId = user?.id;

  const { data, isLoading } = useQuery<ConsentResponse>({
    queryKey: ['/api/legal/consent', userId],
    queryFn: async () => {
      const res = await apiRequest('GET', `/api/legal/consent/${userId}`);
      return res.json();
    },
    enabled: isAuthenticated && !!userId,
    staleTime: 5 * 60 * 1000,
  });

  const recordConsent = useMutation({
    mutationFn: async (documentType: RequiredDocType) => {
      const res = await apiRequest('POST', '/api/legal/consent', {
        documentType,
        documentVersion: data?.currentVersions[documentType],
      });
      return res.json();
    },
  });

  const pending: RequiredDocType[] = useMemo(() => {
    if (!data) return [];
    return REQUIRED_DOCS.filter((doc) => !data.status[doc]);
  }, [data]);

  const needsPrompt = pending.length > 0;
  const allChecked = pending.every((doc) => accepted[doc]);

  // Reset checkbox state whenever the gate opens
  useEffect(() => {
    if (needsPrompt) {
      setAccepted({
        privacy_policy: false,
        terms_of_service: false,
        medical_disclaimer: false,
        cookie_policy: false,
      });
      setError(null);
    }
  }, [needsPrompt]);

  if (!isAuthenticated || isLoading || !needsPrompt) {
    return null;
  }

  const handleAccept = async () => {
    setError(null);
    try {
      await Promise.all(pending.map((doc) => recordConsent.mutateAsync(doc)));
      await queryClient.invalidateQueries({ queryKey: ['/api/legal/consent', userId] });
    } catch (err) {
      console.error('Failed to record consent', err);
      setError(
        language === 'ar'
          ? 'تعذَّر حفظ الموافقة. حاول مرة أخرى.'
          : 'Could not save your consent. Please try again.'
      );
    }
  };

  const isPending = recordConsent.isPending;

  return (
    <Dialog open onOpenChange={() => undefined}>
      <DialogContent
        className={`max-w-lg ${isRTL ? 'rtl font-cairo' : 'ltr'}`}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="dialog-consent-gate"
      >
        <DialogHeader>
          <div className="mb-3 flex justify-center">
            <div className="rounded-full bg-medical-primary/10 p-3">
              <ShieldCheck className="h-7 w-7 text-medical-primary" aria-hidden="true" />
            </div>
          </div>
          <DialogTitle className="text-center text-xl">
            {language === 'ar'
              ? 'تحديث الموافقة على السياسات'
              : 'Please review and accept our policies'}
          </DialogTitle>
          <DialogDescription className="text-center">
            {language === 'ar'
              ? 'لمواصلة استخدام طبيب، يُرجى الموافقة على أحدث إصدار من المستندات القانونية المطلوبة أدناه.'
              : 'To continue using Tabib, please accept the latest version of the required legal documents below.'}
          </DialogDescription>
        </DialogHeader>

        <div className="my-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <p>
              {language === 'ar'
                ? 'تم تحديث الإصدارات الحالية لمستند واحد أو أكثر منذ آخر زيارة لك.'
                : 'One or more documents have been updated since you last accepted them.'}
            </p>
          </div>
        </div>

        <ul className="my-2 space-y-3" data-testid="list-consent-items">
          {pending.map((doc) => {
            const label = DOC_LABELS[doc][language === 'ar' ? 'ar' : 'en'];
            const version = data?.currentVersions[doc];
            return (
              <li key={doc} className="flex items-start gap-3 rtl:space-x-reverse">
                <Checkbox
                  id={`consent-${doc}`}
                  checked={accepted[doc]}
                  onCheckedChange={(checked) =>
                    setAccepted((prev) => ({ ...prev, [doc]: checked === true }))
                  }
                  required
                  data-testid={`checkbox-${doc}`}
                  aria-label={language === 'ar' ? `أوافق على ${label}` : `I agree to the ${label}`}
                />
                <label
                  htmlFor={`consent-${doc}`}
                  className="cursor-pointer text-sm leading-relaxed text-gray-700"
                >
                  {language === 'ar' ? 'لقد قرأت وأوافق على ' : 'I have read and agree to the '}
                  <Link
                    href={DOC_LINKS[doc]}
                    className="font-medium text-medical-primary hover:underline"
                    target="_blank"
                  >
                    {label}
                  </Link>{' '}
                  <span className="text-xs text-gray-500">(v{version})</span>
                </label>
              </li>
            );
          })}
        </ul>

        {error && (
          <p className="mt-2 text-sm text-red-700" role="alert">
            {error}
          </p>
        )}

        <DialogFooter className="mt-4">
          <Button
            onClick={handleAccept}
            disabled={!allChecked || isPending}
            className="w-full bg-medical-primary hover:bg-blue-700"
            data-testid="button-accept-policies"
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            {language === 'ar' ? 'أوافق وأتابع' : 'Accept & Continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
